/** @jsx jsx */
/** @jsxFrag */
import React, { type MutableRefObject, memo, type ReactNode, useState } from 'react';
import { css, keyframes, jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Pressable, xcss } from '@atlaskit/primitives';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { useCellHoverCallbacks, useCellHoverHandlers } from '../controllers/index.tsx';

type MinimumWidth = 'narrow';

export type Props = {
	content?: ReactNode;
	children: ReactNode;
	contentRef?: MutableRefObject<HTMLElement | null>;
	minimumWidth?: MinimumWidth;
	entryPointActions?: EntryPointActions;
	isMoreItems?: boolean;
	shouldGetParentRef?: boolean;
};

export const HoverPopover = memo(
	({
		content,
		children,
		contentRef,
		minimumWidth,
		entryPointActions,
		isMoreItems,
		shouldGetParentRef,
	}: Props) => {
		const [isPopupOpen, setIsPopupOpen] = useState(false);

		useCellHoverCallbacks({
			contentRef,
			onPreload: entryPointActions?.preload,
			onLoad: entryPointActions?.load,
			onPopupToggle: setIsPopupOpen,
			shouldGetParentRef,
		});

		if (!content) {
			return children;
		}

		const contentWithPopover = (
			<>
				{children}
				{content && (
					<div
						css={[
							containerStyles,
							containerVariantStyles[minimumWidth ?? 'content'],
							isMoreItems ? moreItemsTop : normalTop,
						]}
					>
						<div
							data-testid="hover-popover.ui.hover-popover-content"
							aria-hidden
							css={popoverStyles}
						>
							{content}
						</div>
					</div>
				)}
			</>
		);

		return isPopupOpen ? contentWithPopover : children;
	},
);

export type HoverMoreItemsTrigger = {
	content?: ReactNode;
	numItems: number;
	entryPointActions?: EntryPointActions;
};

export const HoverMoreItemsTrigger = ({
	numItems,
	content,
	entryPointActions,
}: HoverMoreItemsTrigger) => {
	const handlers = useCellHoverHandlers();

	return (
		<Pressable
			xcss={itemsCountStyles}
			testId="hover-popover.ui.hover-trigger"
			onMouseEnter={handlers.handleMouseEnter}
			onMouseLeave={handlers.handleMouseLeave}
		>
			{/* eslint-disable-next-line jira/i18n/text-wrapped-in-format-message */}
			<HoverPopover content={content} entryPointActions={entryPointActions} isMoreItems>
				+{numItems}
			</HoverPopover>
		</Pressable>
	);
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const containerStyles = css({
	paddingTop: token('space.050'),
	position: 'absolute',
	top: '40px',
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	zIndex: 9999,
});

const normalTop = css({
	top: '40px',
});

const moreItemsTop = css({
	top: '20px',
});

const containerVariantStyles = cssMap({
	narrow: {
		minWidth: '200px',
		maxWidth: '300px',
	},
	content: {
		whiteSpace: 'nowrap',
	},
});

const popoverStyles = css({
	backgroundColor: token('elevation.surface.overlay'),
	paddingInline: token('space.150'),
	paddingBlock: token('space.075'),
	borderRadius: token('border.radius'),
	boxShadow: token('elevation.shadow.overlay'),
	fontBody: token('font.body'),
	fontWeight: token('font.weight.regular'),
	overflowWrap: 'break-word',
	maxHeight: '300px',
	overflowY: 'scroll',
});

const itemsCountStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle.hovered',
	borderRadius: 'border.radius',
	position: 'relative',
	height: token('space.250'),
	paddingInline: 'space.050',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});
